import React, { useState, useEffect } from 'react'
import { Form, Field } from 'react-final-form'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import { get as _get } from 'lodash'
import { Upload, CheckCircle2, AlertCircle, Info, Download } from 'lucide-react'

import { Button } from '../ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card'
import DropToUploadInput from '../leevo_ui/DropToUploadInput/DropToUploadInput'
import { requestCreator } from '../../utilities/requests'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import Notification from '../leevo_ui/Notification/Notification'
import useFacility from '../../utilities/hooks/useFacility'

const { get: newImport, post: createImport, cancel: cancelImportRequests } = requestCreator()

function StudentImportsNew() {
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const [isLoading, setIsLoading] = useState(false)
  const [isImportPending, setIsImportPending] = useState(false)
  const { replace: redirect } = useHistory()
  const [notification, setNotification] = useState({
    success: null,
    message: null
  })

  async function onSubmit(values) {
    const formData = new FormData()

    Object.entries(values).forEach(([key, value]) => formData.append(key, value))

    setIsImportPending(true)
    return createImport(`/api/facilities/${facility.id}/student_imports`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'multipart/form-data'
      }
    })
      .then(() => {
        setNotification({
          success: true,
          message: 'Import processed successfully! Students and guardians have been created.'
        })
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          let message = _get(error, 'response.data.error')
          if (!message) {
            message = _get(error, 'response.data.errors')
              ? renderedErrors(_get(error, 'response.data.errors'))
              : 'Import failed'
          }
          setNotification({ success: false, message })
        }
      })
      .finally(() => setIsImportPending(false))
  }

  function renderedErrors(errors) {
    return (
      <div className="space-y-1">
        <p className="font-semibold">Errors found in CSV:</p>
        {Object.keys(errors).map((key) => (
          <p key={key} className="text-sm">
            Row {key}: {errors[key]}
          </p>
        ))}
      </div>
    )
  }

  function downloadTemplate() {
    const headers = [
      'First Name',
      'Last Name',
      'Guardian Email',
      'Birthdate',
      'Gender',
      'LTS Number',
      'Address Line 1',
      'Address Line 2',
      'City',
      'State',
      'Post Code',
      'Guardian First Name',
      'Guardian Last Name',
      'Skill Level',
      'Invited By'
    ]

    const exampleRow = [
      'John',
      'Doe',
      'guardian@example.com',
      '2015-05-15',
      'male',
      '12345',
      '123 Main St',
      'Apt 4',
      'Springfield',
      'IL',
      '62701',
      'Jane',
      'Doe',
      'Basic 1',
      'director@facility.com'
    ]

    const csvContent = [headers.join(','), exampleRow.join(',')].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'student_import_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useEffect(() => {
    if (!isFacilityLoading) {
      setIsLoading(true)
      newImport(`/api/facilities/${facility.id}/student_imports/new`)
        .then(() => setIsLoading(false))
        .catch((error) => {
          if (!axios.isCancel(error))
            redirect({
              pathname: '/',
              state: {
                notification: {
                  message: "You're not allowed to import students.",
                  color: 'red'
                }
              }
            })
        })
    }
    return cancelImportRequests
  }, [redirect, facility.id, isFacilityLoading])

  return (
    <ApplicationLayout isContentLoading={isFacilityLoading || isLoading}>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Import Students</h1>
          <p className="mt-2 text-gray-600">Bulk import students and their guardians using a CSV file</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload CSV File
              </CardTitle>
              <CardDescription>Select or drag and drop your CSV file to import students</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Form
                initialValues={{ file: null }}
                onSubmit={onSubmit}
                render={({ handleSubmit }) => (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <Field name="file" allowNull>
                      {({ input }) => <DropToUploadInput {...input} id="upload" />}
                    </Field>
                    <Button type="submit" disabled={isImportPending} className="w-full">
                      {isImportPending ? 'Importing...' : 'Import Students'}
                    </Button>
                  </form>
                )}
              />

              <Notification show={notification.message} color={notification.success ? 'green' : 'red'}>
                {notification.message}
              </Notification>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Instructions
              </CardTitle>
              <CardDescription>Follow these steps to import students successfully</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">Download the template</p>
                    <p className="text-sm text-gray-600">Start with our CSV template to ensure correct formatting</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">Fill in student information</p>
                    <p className="text-sm text-gray-600">Add one student per row with their details</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">Save as CSV</p>
                    <p className="text-sm text-gray-600">Export your file as CSV format before uploading</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">Upload and import</p>
                    <p className="text-sm text-gray-600">Upload your file and click Import Students</p>
                  </div>
                </div>
              </div>

              <Button variant="outline" onClick={downloadTemplate} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>CSV Format Requirements</CardTitle>
            <CardDescription>Your CSV file must include these columns</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  Required Columns
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-red-900">First Name</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-red-900">Last Name</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-red-900">Guardian Email</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Optional Columns</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Birthdate</p>
                    <p className="text-xs text-gray-500">YYYY-MM-DD format</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Gender</p>
                    <p className="text-xs text-gray-500">male/female/other</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">LTS Number</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Address Line 1</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Address Line 2</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">City</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">State</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Post Code</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Guardian First Name</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Guardian Last Name</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Skill Level</p>
                    <p className="text-xs text-gray-500">e.g., Basic 1</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">Invited By</p>
                    <p className="text-xs text-gray-500">Director&apos;s email</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">What happens during import?</h4>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li>• Students will be created with the provided information</li>
                  <li>• Guardians will be created if they don&apos;t exist (using Guardian Email)</li>
                  <li>• Students will be linked to their guardians automatically</li>
                  <li>• Guardian invitations will be sent via email</li>
                  <li>• If a complete address is provided, it will be saved for the student</li>
                  <li>• Skill levels will be assigned if a matching level exists</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ApplicationLayout>
  )
}

export default StudentImportsNew
